/**
 * API client.
 *
 * Two auth modes behind one call site. In development the acting user is named
 * by the `x-aura-user` header; under SSO the same client sends a bearer token
 * instead. Which one applies is decided once at boot and installed here, so no
 * caller anywhere else has to know.
 */

const BASE = '/api/v1';

/** Installed at boot by the session bootstrap; null means development mode. */
let tokenProvider: (() => Promise<string | null>) | null = null;

export function useBearerTokens(provider: () => Promise<string | null>): void {
  tokenProvider = provider;
}

/** The dev user switcher persists its choice so a reload keeps the same identity. */
const ACTING_USER_KEY = 'aura.actingUser';

export function getActingUser(): string | null {
  return localStorage.getItem(ACTING_USER_KEY);
}

export function setActingUser(email: string | null): void {
  if (email) localStorage.setItem(ACTING_USER_KEY, email);
  else localStorage.removeItem(ACTING_USER_KEY);
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** §35: the server asks for a reason rather than refusing the promise. */
  get requiresOverrideReason(): boolean {
    return this.details?.requiresOverrideReason === true;
  }

  get requiresReason(): boolean {
    return this.details?.requiresReason === true;
  }

  get warning(): string | null {
    return typeof this.details?.warning === 'string' ? this.details.warning : null;
  }

  get findings(): string[] {
    return Array.isArray(this.details?.findings) ? (this.details.findings as string[]) : [];
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.pathname + url.search;
}

/**
 * Who the request is from. Extracted so that anything reaching the API — not
 * only the JSON helpers below — makes the same dev-header/bearer-token decision.
 */
async function identityHeaders(): Promise<Record<string, string>> {
  if (tokenProvider) {
    // Asked for per request rather than cached here: MSAL renews silently, and
    // a token held at boot would expire under a tab left open all day.
    const token = await tokenProvider();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  const actingUser = getActingUser();
  return actingUser ? { 'x-aura-user': actingUser } : {};
}

async function request<T>(
  method: string,
  path: string,
  options: { query?: Query; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await identityHeaders()),
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const body = parsed as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `Request failed with status ${response.status}`,
      body?.error?.details,
      body?.error?.requestId,
    );
  }

  return parsed as T;
}

/**
 * Fetches a file and hands it to the browser to save.
 *
 * Separate from `request` because the response is a blob rather than JSON, but
 * it goes through `identityHeaders` for exactly the same reason every other call
 * does — the endpoint is authenticated, and a bare fetch here would send no
 * credentials at all.
 */
async function download(path: string, filename: string): Promise<void> {
  const response = await fetch(buildUrl(path), {
    headers: { Accept: 'text/csv', ...(await identityHeaders()) },
  });

  if (!response.ok) {
    const text = await response.text();
    let body: ApiErrorBody | null = null;
    try {
      body = text ? (JSON.parse(text) as ApiErrorBody) : null;
    } catch {
      body = null;
    }
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `Could not download ${filename}.`,
      body?.error?.details,
      body?.error?.requestId,
    );
  }

  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    // Revoking immediately is safe: the click has already handed the blob over.
    URL.revokeObjectURL(url);
  }
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  download,
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
